import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { IoMdMegaphone } from 'react-icons/io';
import { FiPlus, FiSearch, FiSliders, FiUser, FiBell, FiRefreshCw, FiEdit2, FiEye, FiCopy, FiGrid, FiList, FiFileText, FiInfo } from 'react-icons/fi';
import { ModalShell } from './CampaignModals';
import { Template } from '../../../model/Template';
import { IGlobalState } from '../../../model/IGlobalState';
import api from '../../../api/api';
import { to } from '../../../util/util';
import Stock from '../../../model/Stock';
import { toast } from '../Toast';

// Welcome email is the template with this name (WelcomeEmailEventHandler)
export const welcomeTemplateName = 'Welcome Email';
export const welcomeMessageDetail = 'Replaced with the personal welcome message you add when sending; removed for a website signup.';

export const Thumbnail: React.FC<{ html: string; className?: string }> = ({ html, className }) =>
    <div className={`thumb ${className || ''}`}>
        <iframe title="preview" srcDoc={html} sandbox="" tabIndex={-1} scrolling="no" />
    </div>;

export const previewPath = (id: string) => `/communications/email-templates/${encodeURIComponent(id)}/preview`;
export const newTemplatePath = '/communications/email-templates/new';

// v1 test send: the saved template, filled from the stock's newest announcement; empty list / reply-to / from / client → the stock's own
export const sendTemplateTest = async (stock: Stock, templateId: string, testEmailAddresses: string): Promise<boolean> => {
    const [err, result] = await to(api().sendTestEmail({ apiKey: stock.apiKey, listId: [], templateId, replyTo: '', fromName: '', clientId: '', testEmailAddresses }));
    if (err || result.errorMessage) { toast.error(`Test failed: ${err?.message || result.errorMessage}`); return false; }
    toast.success(`Test sent to ${testEmailAddresses}`);
    return true;
};
export const editPath = (id: string) => `/communications/email-templates/${encodeURIComponent(id)}/edit`;

// spec 2:63853, announcements only (welcome is edited in place; choosing a notifications template is omitted, beyond v1)
export const ChooseAnnouncementTemplateModal: React.FC<{ templates: Template[]; currentId: string; selectedId?: string; onClose: () => void; onSave: (id: string) => Promise<boolean> }> = ({ templates, currentId, selectedId, onClose, onSave }) => {
    const history = useHistory();
    const [selected, setSelected] = useState(selectedId || currentId);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        const ok = await onSave(selected);
        if (ok) onClose(); else setSaving(false);
    };

    return <ModalShell icon={<IoMdMegaphone />} title="Which Template Should ASX Announcements Use?" sub="Every announcement email will look like the one you pick. It takes effect from the next announcement." note="" onClose={onClose}
        buttons={<>
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={!selected || selected === currentId || saving}>{saving ? 'Saving…' : 'Use This Template'}</button>
        </>}>
        <div className="template-options">
            {templates.map(t => <label key={t.id!} className={`option${t.id === selected ? ' on' : ''}`}>
                <Thumbnail html={t.html} className="mini" />
                <span className="name">{t.name}</span>
                {t.id === currentId ? <span className="pill info">Assigned · in use now</span> : <span className="pill">Not assigned</span>}
                <button type="button" className="btn-outline sm" onClick={e => { e.preventDefault(); onClose(); history.push(previewPath(t.id!)); }}><FiEye /> Preview</button>
                <input type="radio" name="announcement-template" checked={t.id === selected} onChange={() => setSelected(t.id!)} />
            </label>)}
        </div>
        <div className="info-note"><FiInfo /> Announcement emails already sent keep the template they were sent with. This only changes what happens from now on.</div>
    </ModalShell>;
};

const EmailTemplates: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
    const [search, setSearch] = useState('');
    const history = useHistory();
    const [view, setView] = useState<'grid' | 'list'>('grid');
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [all, setAll] = useState<Template[] | null>(null);
    const [announcementTemplateId, setAnnouncementTemplateId] = useState('');
    const [error, setError] = useState('');
    const [choosing, setChoosing] = useState(false);
    const [copying, setCopying] = useState(false);

    // a copy keeps the HTML, campaign flag and layout (TemplatesController.GetNewTemplate), then opens in the builder
    const duplicate = async (t: Template) => {
        setCopying(true);
        const [err, result] = await to(api().createEmailTemplate({ stockId: stock.id!, name: `${t.name} (copy)`, copyOfId: t.id! }));
        setCopying(false);
        if (err || !result.templateId) { toast.error(err?.message || result?.errorMessage || 'Failed to copy the template'); return; }
        onChanged?.();
        history.push(editPath(result.templateId));
    };

    const saveAnnouncementTemplate = async (id: string) => {
        const [err, result] = await to(api().saveTemplateSettings({ stockId: stock.id!, announcementTemplateId: id }));
        if (err || !result.success) {
            setError(err?.message || result?.errorMessage || 'Failed to save');
            return false;
        }
        setAnnouncementTemplateId(id);
        return true;
    };

    useAsyncEffect(async () => {
        if (!stock?.id) return;
        setAll(null);
        setError('');
        const [err, data] = await to(Promise.all([api().getTemplates(stock.id), api().getEmailMarketingSettings(stock.id)]));
        if (err) { setError(err.message || 'Failed to load templates'); return; }
        const [templatesResult, settings] = data;
        setAnnouncementTemplateId(settings.mailChimpTemplateId || '');
        setAll(templatesResult.templates);
    }, [stock?.id]);

    const templates = (all || []).filter(t => t.name.toLowerCase().includes(search.trim().toLowerCase()));
    const announcement = all?.find(t => t.id === announcementTemplateId);
    const welcome = all?.find(t => t.name === welcomeTemplateName);

    const descriptionOf = (t: Template) => t.id === announcementTemplateId ? 'Used by every announcement email'
        : t.name === welcomeTemplateName ? 'Sent when you invite a holder'
            : t.isForCustomCampaign ? 'For campaigns you write yourself' : '';

    const usedFor = (t: Template) => t.id === announcementTemplateId
        ? <span className="pill info"><IoMdMegaphone /> Used for ASX announcements</span>
        : t.name === welcomeTemplateName
            ? <span className="pill info"><FiUser /> Used for Welcome email</span>
            : <span className="pill">Not assigned</span>;

    const actions = (t: Template) => <div className="acts">
        <button type="button" className="btn-primary sm" onClick={() => history.push(editPath(t.id!))}><FiEdit2 /> Edit</button>
        <button type="button" className="btn-outline sm" onClick={() => history.push(previewPath(t.id!))}><FiEye /> Preview</button>
        <button type="button" className="btn-outline sm" disabled={copying} onClick={() => duplicate(t)}><FiCopy /> {copying ? 'Copying…' : 'Duplicate'}</button>
    </div>;

    const role = (icon: React.ReactNode, title: string, sub: string, current: React.ReactNode, button: React.ReactNode) =>
        <div className="role">
            <div className="rh">{icon} {title}</div>
            <div className="sub">{sub}</div>
            <div className="current">{current}</div>
            {button}
        </div>;

    const current = (t: Template | undefined, fallback: string) =>
        <>
            {t ? <Thumbnail html={t.html} className="mini" /> : <span className="mini-tile"><FiFileText /></span>}
            {all
                ? <div><div className="name">{t?.name ?? fallback}</div><div className="in-use">{t || fallback !== 'None' ? 'in use' : 'not set'}</div></div>
                : <div className="in-use">Loading…</div>}
        </>;

    return <div className="email-templates">
        <div className="page-header">
            <div>
                <h1>Email Templates</h1>
                <p>How your emails look — the announcement, welcome and campaign templates</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-primary" onClick={() => history.push(newTemplatePath)}><FiPlus /> Create a Template</button>
            </div>
        </div>

        <div className="card roles-card">
            <div className="h">
                <span className="icon-tile"><FiSliders /></span>
                <div>
                    <div className="t">Which template is used where</div>
                    <div className="d">Investi sends three kinds of email on its own. Choose the template each one uses.</div>
                </div>
            </div>
            <div className="roles">
                {role(<IoMdMegaphone />, 'ASX announcements', 'Sent automatically when an announcement is released',
                    current(announcement, 'None'),
                    <button type="button" className="btn-outline sm" disabled={!all?.length} onClick={() => setChoosing(true)}><FiRefreshCw /> Change</button>)}
                {role(<FiUser />, 'Welcome email', 'Sent when you invite a holder to the Website Hub',
                    current(welcome, 'None'),
                    <button type="button" className="btn-outline sm" disabled={!welcome} onClick={() => welcome && history.push(editPath(welcome.id!))}><FiEdit2 /> Edit</button>)}
                {role(<FiBell />, 'Notifications', 'Confirm your email, question answered',
                    current(undefined, stock?.announcementCommentTemplate ? 'Custom' : 'Investi default'),
                    null)}
            </div>
        </div>

        <div className="card toolbar">
            <label className="search"><FiSearch /><input placeholder="Search templates…" value={search} onChange={e => setSearch(e.target.value)} /></label>
            <div className="seg">
                <button type="button" className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}><FiGrid /> Thumbnails</button>
                <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}><FiList /> List</button>
            </div>
        </div>

        {error && <p className="error">{error}</p>}
        {!all && !error && <p className="loading">Loading templates…</p>}
        {all && templates.length === 0 && <p className="empty">{all.length ? 'No templates match' : 'No templates yet'}</p>}

        {view === 'grid'
            ? <div className="cards">
                {templates.map(t => <div key={t.id!} className="tcard">
                    <Thumbnail html={t.html} />
                    <div className="name">{t.name}</div>
                    <div className="desc">{descriptionOf(t)}</div>
                    <div className="badge">{usedFor(t)}</div>
                    {actions(t)}
                </div>)}
            </div>
            : <div className="card list">
                {templates.map(t => <div key={t.id!} className="lrow">
                    <Thumbnail html={t.html} className="mini" />
                    <div className="info">
                        <div className="name">{t.name}</div>
                        <div className="desc">{descriptionOf(t)}</div>
                    </div>
                    <div className="badge">{usedFor(t)}</div>
                    {actions(t)}
                </div>)}
            </div>}

        <div className="foot">
            <span>Templates decide how every email looks. Start a new one from a layout, or duplicate one you already have.</span>
            <button type="button" className="btn-outline" onClick={() => history.push(newTemplatePath)}><FiPlus /> Create a Template</button>
        </div>

        {choosing && all && <ChooseAnnouncementTemplateModal templates={all.filter(t => t.name !== welcomeTemplateName)} currentId={announcementTemplateId}
            onClose={() => setChoosing(false)} onSave={saveAnnouncementTemplate} />}
    </div>;
};

export default EmailTemplates;
